<script setup>
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from "vue"
import { useRouter } from "vue-router"
import { api } from "./api"

const me = ref(null)
const dark = ref(localStorage.getItem("theme") === "dark")
const mobileMenu = ref(false)
const sidebarExpanded = ref(localStorage.getItem("sidebar-expanded") === "true")
const locale = ref("ru")
const router = useRouter()
const search = ref(""), searchResults = ref([]), searchOpen = ref(false), searchLoading = ref(false)
let searchTimer
const messages = {
  ru: { dashboard: "Дашборд", partners: "Партнёры", tasks: "Задачи", reports: "Отчёты", imports: "Импорт", chats: "AI-чат", monitoring: "Мониторинг", settings: "Настройки", search: "Поиск" },
  en: { dashboard: "Dashboard", partners: "Partners", tasks: "Tasks", reports: "Reports", imports: "Imports", chats: "AI chat", monitoring: "Monitoring", settings: "Settings", search: "Search" }
}
provide("t", (key) => messages[locale.value][key] || key)
provide("locale", locale)
provide("me", me)

function toggleTheme() {
  dark.value = !dark.value
  localStorage.setItem("theme", dark.value ? "dark" : "light")
}
function toggleSidebar() { sidebarExpanded.value = !sidebarExpanded.value; localStorage.setItem("sidebar-expanded", String(sidebarExpanded.value)) }
const groupedResults = computed(() => ({
  partners: searchResults.value.filter(item => item.type === "Partner"),
  tasks: searchResults.value.filter(item => item.type === "CrmTask")
}))
watch(search, value => {
  window.clearTimeout(searchTimer)
  if (value.trim().length < 2) { searchResults.value = []; searchOpen.value = Boolean(value); return }
  searchLoading.value = true
  searchTimer = window.setTimeout(async () => {
    try { searchResults.value = await api(`/entities?q=${encodeURIComponent(value.trim())}&type[]=Partner&type[]=CrmTask`); searchOpen.value = true }
    finally { searchLoading.value = false }
  }, 220)
})
function openResult(item) {
  search.value = ""; searchOpen.value = false
  router.push(item.type === "Partner" ? `/partners/${item.id}` : `/tasks/${item.id}`)
}
function closeSearch(event) { if (!event.target.closest(".global-search")) searchOpen.value = false }
function searchKeyboard(event) { if (event.key === "Escape") { searchOpen.value = false; event.target.blur() } }
onMounted(async () => { me.value = await api("/me"); document.addEventListener("click", closeSearch) })
onBeforeUnmount(() => { document.removeEventListener("click", closeSearch); window.clearTimeout(searchTimer) })
</script>

<template>
  <div class="crm-shell" :class="{ dark }">
    <aside class="sidebar" :class="{ open: mobileMenu, expanded: sidebarExpanded }">
      <div class="brand"><span class="brand-mark">A</span><span class="nav-label">Affiliate CRM</span><button class="sidebar-toggle" :title="sidebarExpanded ? 'Свернуть меню' : 'Развернуть меню'" @click="toggleSidebar">{{ sidebarExpanded ? '‹' : '›' }}</button></div>
      <nav @click="mobileMenu = false">
        <RouterLink to="/dashboard" :title="messages[locale].dashboard"><span>▦</span><b class="nav-label">{{ messages[locale].dashboard }}</b></RouterLink>
        <RouterLink to="/partners" :title="messages[locale].partners"><span>♙</span><b class="nav-label">{{ messages[locale].partners }}</b></RouterLink>
        <RouterLink to="/tasks" :title="messages[locale].tasks"><span>✓</span><b class="nav-label">{{ messages[locale].tasks }}</b></RouterLink>
        <RouterLink to="/reports" :title="messages[locale].reports"><span>↗</span><b class="nav-label">{{ messages[locale].reports }}</b></RouterLink>
        <RouterLink to="/imports" :title="messages[locale].imports"><span>⇄</span><b class="nav-label">{{ messages[locale].imports }}</b></RouterLink>
        <RouterLink to="/chats" :title="messages[locale].chats"><span>✦</span><b class="nav-label">{{ messages[locale].chats }}</b></RouterLink>
        <a v-if="me?.role === 'admin'" href="/rails_pulse" :title="messages[locale].monitoring"><span>⌁</span><b class="nav-label">{{ messages[locale].monitoring }}</b></a>
        <RouterLink v-if="me?.role === 'admin'" to="/settings" :title="messages[locale].settings"><span>⚙</span><b class="nav-label">{{ messages[locale].settings }}</b></RouterLink>
      </nav>
      <div class="sidebar-footer">
        <div class="avatar">{{ me?.name?.slice(0, 1) || "?" }}</div>
        <div class="nav-label"><strong>{{ me?.name }}</strong><small>{{ me?.role }}</small></div>
      </div>
    </aside>
    <div class="workspace" :class="{ 'sidebar-expanded': sidebarExpanded }">
      <header class="topbar">
        <button class="icon-button mobile-only" @click="mobileMenu = !mobileMenu">☰</button>
        <div class="top-search global-search" :class="{active:searchOpen}">⌕ <input v-model="search" placeholder="Поиск партнёров и задач…" @focus="searchOpen=true" @keydown="searchKeyboard" />
          <div v-if="searchOpen" class="global-search-results">
            <div v-if="search.length < 2" class="global-search-empty">Введите минимум 2 символа</div>
            <div v-else-if="searchLoading" class="global-search-empty">Ищем…</div>
            <template v-else>
              <section v-if="groupedResults.partners.length"><header>Партнёры <span>{{ groupedResults.partners.length }}</span></header><button v-for="item in groupedResults.partners" :key="`partner-${item.id}`" @click="openResult(item)"><strong>{{ item.title }}</strong><small>{{ item.subtitle || 'Без категории' }}</small></button></section>
              <section v-if="groupedResults.tasks.length"><header>Задачи <span>{{ groupedResults.tasks.length }}</span></header><button v-for="item in groupedResults.tasks" :key="`task-${item.id}`" @click="openResult(item)"><strong>{{ item.title }}</strong><small>{{ item.subtitle }}</small></button></section>
              <div v-if="!groupedResults.partners.length && !groupedResults.tasks.length" class="global-search-empty">Ничего не найдено</div>
            </template>
          </div>
        </div>
        <div class="top-actions">
          <button class="icon-button" @click="toggleTheme">{{ dark ? "☀" : "☾" }}</button>
        </div>
      </header>
      <main><RouterView /></main>
    </div>
  </div>
</template>
